import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  numeroSegonsEntreDates,
  convertirSegonsAMinuts,
  convertirSegonsaHores,
  numeroSegonsEntreDates2
} from '../../Helpers/funcionsDates';

// Llegeix directament de cp.detallSetmana (ja carregat per HoresTotalsSetmana)
// 0 crides HTTP addicionals

const ContadorHoresJornada = ({ contingut }) => {
  const { cp } = useSelector(state => ({ cp: state.cp }));
  const { t } = useTranslation('generic');

  const [contadorJornadaIniciada, setContadorJornadaIniciada] = useState(new Date());

  // Actualitzar el contador cada 10 segons per mostrar hores en temps real
  useEffect(() => {
    if (!contingut) return;
    const interval = setInterval(() => setContadorJornadaIniciada(new Date()), 10000);
    return () => clearInterval(interval);
  }, [contingut]);

  const diaAvui = moment().format('YYYY-MM-DD');

  // Fixatges i incidències d'avui des de detallSetmana
  const fixatgesAvui = useMemo(() =>
    cp?.detallSetmana?.fixatges_setmanals?.find(d => d.dia === diaAvui)?.fixatges ?? []
  , [cp?.detallSetmana, diaAvui]);

  const incidenciesAvui = useMemo(() =>
    cp?.detallSetmana?.llistatIncidencies?.filter(e => {
      const inici = moment(e.DataInici_IncidenciaFix).format('YYYY-MM-DD');
      const fi    = moment(e.DataFi_IncidenciaFix).format('YYYY-MM-DD');
      return diaAvui >= inici && diaAvui <= fi;
    }) ?? []
  , [cp?.detallSetmana, diaAvui]);

  // Determinar estat de la jornada des dels fixatges
  const { jornadaIniciada, jornadaFinalitzada } = useMemo(() => {
    if (!fixatgesAvui.length) return { jornadaIniciada: false, jornadaFinalitzada: false };
    const ultim = fixatgesAvui[fixatgesAvui.length - 1];
    if (ultim.IdTipus_Fixatge === '1' || ultim.IdTipus_Fixatge === '2') {
      return { jornadaIniciada: true, jornadaFinalitzada: false };
    }
    return { jornadaIniciada: false, jornadaFinalitzada: true };
  }, [fixatgesAvui]);

  const { horesTreballades, minutsTreballats } = useMemo(() => {
    if (!contingut || (!jornadaIniciada && !jornadaFinalitzada)) {
      return { horesTreballades: 0, minutsTreballats: 0 };
    }

    // Hores justificades per incidències
    let segonsJustificats = 0;
    incidenciesAvui.forEach(e => {
      if (e.IdTipusIncidencia_IncidenciaFix !== '6' && e.IdTipusIncidencia_IncidenciaFix !== '8') {
        if (e.Justificat_IncidenciaFix === '1') {
          const durada = numeroSegonsEntreDates(e.DataInici_IncidenciaFix, e.DataFi_IncidenciaFix);
          if ([1, 4, 12].includes(Number(e.IdTipusIncidencia_IncidenciaFix))) {
            segonsJustificats += durada;
          } else {
            const limit = (contingut.computHores / 5) * 60 * 60 * 1000;
            segonsJustificats += Math.min(durada, limit);
          }
        }
      }
    });

    // Hores treballades des dels fixatges
    let segonsTotalJornada = 0;
    for (let i = 0; i < fixatgesAvui.length; i += 2) {
      const entrada = fixatgesAvui[i];
      const sortida = fixatgesAvui[i + 1];
      if (!sortida) break;
      if (['1', '2'].includes(entrada.IdTipus_Fixatge)) {
        segonsTotalJornada += numeroSegonsEntreDates(entrada.DataHora_Fixatge, sortida.DataHora_Fixatge);
      }
    }
    // Última entrada sense sortida → comptem fins ara
    if (fixatgesAvui.length % 2 === 1) {
      const ultima = fixatgesAvui[fixatgesAvui.length - 1];
      segonsTotalJornada += numeroSegonsEntreDates2(ultima.DataHora_Fixatge, contadorJornadaIniciada);
    }
    segonsTotalJornada += segonsJustificats;

    return {
      horesTreballades: convertirSegonsaHores(segonsTotalJornada),
      minutsTreballats: convertirSegonsAMinuts(segonsTotalJornada)
    };
  }, [fixatgesAvui, incidenciesAvui, jornadaIniciada, jornadaFinalitzada, contingut, contadorJornadaIniciada]);

  if (!jornadaIniciada && !jornadaFinalitzada) return null;

  return (
    <p className='alert alert-soft-dark'>
      {t('ResumHores.resumAvui')}{' '}
      <strong>
        {horesTreballades} {t('Generics.hores')} {t('Generics.caracIoY')} {minutsTreballats} {t('Generics.minuts')}
      </strong>
      <br />
      <small>{contadorJornadaIniciada.toLocaleString()}</small>
    </p>
  );
};

export default React.memo(ContadorHoresJornada);